// Centraliseret definition af felter brugere kan hjælpe med at udfylde.
// Bruges af MissingFieldsBox på CarDetail-siden.

export type FieldScope = 'variant' | 'specs' | 'price';
export type FieldInputType = 'number' | 'text' | 'date' | 'boolean';

export interface MissingFieldDef {
  key: string;
  label: string;
  type: FieldInputType;
  scope: FieldScope;
  unit?: string;
  hint?: string;
  evOnly?: boolean;
  iceOnly?: boolean;
}

// Variant-tabellen
const VARIANT_FIELDS: MissingFieldDef[] = [
  { key: 'curb_weight_kg', label: 'Egenvægt', type: 'number', scope: 'variant', unit: 'kg', hint: 'Elbilens vægt uden last (typisk 1.500-2.500 kg).' },
  { key: 'trunk_l', label: 'Bagagerum', type: 'number', scope: 'variant', unit: 'L', hint: 'Bagagerumsvolumen i liter med bagsæder oppe.' },
  { key: 'frunk_liter', label: 'Frunk (forrumsbagagerum)', type: 'number', scope: 'variant', unit: 'L', hint: 'Volumen af bagagerum foran (kun hvis bilen har et).' },
  { key: 'seats', label: 'Antal sæder (standard)', type: 'number', scope: 'variant' },
  { key: 'doors', label: 'Antal døre', type: 'number', scope: 'variant' },
  { key: 'length_m', label: 'Længde', type: 'number', scope: 'variant', unit: 'm', hint: 'Total længde i meter (fx 4.85).' },
  { key: 'width_m', label: 'Bredde', type: 'number', scope: 'variant', unit: 'm' },
  { key: 'height_m', label: 'Højde', type: 'number', scope: 'variant', unit: 'm' },
  { key: 'wheelbase_m', label: 'Akselafstand', type: 'number', scope: 'variant', unit: 'm' },
  { key: 'turning_diameter_m', label: 'Vendediameter', type: 'number', scope: 'variant', unit: 'm' },
  { key: 'warranty_years', label: 'Garanti (år)', type: 'number', scope: 'variant', unit: 'år' },
  { key: 'warranty_km', label: 'Garanti (km)', type: 'number', scope: 'variant', unit: 'km' },
  { key: 'battery_warranty_years', label: 'Batterigaranti (år)', type: 'number', scope: 'variant', unit: 'år', evOnly: true },
  { key: 'battery_warranty_km', label: 'Batterigaranti (km)', type: 'number', scope: 'variant', unit: 'km', evOnly: true },
  { key: 'green_owner_tax_yearly_dkk', label: 'CO₂-ejerafgift pr. år', type: 'number', scope: 'variant', unit: 'kr.' },
];

// Powertrain_specs-tabellen — fælles
const SHARED_SPEC_FIELDS: MissingFieldDef[] = [
  { key: 'power_hp', label: 'Effekt', type: 'number', scope: 'specs', unit: 'hk' },
  { key: 'torque_nm', label: 'Drejningsmoment', type: 'number', scope: 'specs', unit: 'Nm' },
  { key: 'acceleration_0_100', label: '0-100 km/t', type: 'number', scope: 'specs', unit: 'sek' },
  { key: 'top_speed_kmh', label: 'Tophastighed', type: 'number', scope: 'specs', unit: 'km/t' },
];

// EV-specifikke
const EV_SPEC_FIELDS: MissingFieldDef[] = [
  { key: 'battery_kwh', label: 'Batteri (netto)', type: 'number', scope: 'specs', unit: 'kWh', evOnly: true },
  { key: 'range_wltp_km', label: 'WLTP-rækkevidde', type: 'number', scope: 'specs', unit: 'km', evOnly: true },
  { key: 'consumption_wh_km', label: 'Forbrug', type: 'number', scope: 'specs', unit: 'Wh/km', evOnly: true },
  { key: 'charging_ac_kw', label: 'AC-opladning maks.', type: 'number', scope: 'specs', unit: 'kW', evOnly: true },
  { key: 'charging_dc_kw', label: 'DC-opladning maks.', type: 'number', scope: 'specs', unit: 'kW', evOnly: true },
  { key: 'dc_charge_10_80_min', label: 'DC 10-80%', type: 'number', scope: 'specs', unit: 'min', evOnly: true },
];

// Pris
const PRICE_FIELDS: MissingFieldDef[] = [
  { key: 'price_dkk', label: 'Pris (DKK)', type: 'number', scope: 'price', unit: 'kr.', hint: 'Vejledende udsalgspris i Danmark.' },
];

export const ALL_MISSING_FIELDS: MissingFieldDef[] = [
  ...VARIANT_FIELDS,
  ...SHARED_SPEC_FIELDS,
  ...EV_SPEC_FIELDS,
  ...PRICE_FIELDS,
];

/** Returner felter der er relevante for variantens powertrain. */
export function getRelevantFields(powertrainType: string | null | undefined): MissingFieldDef[] {
  const isEV = powertrainType === 'EV';
  return ALL_MISSING_FIELDS.filter(f => {
    if (f.evOnly && !isEV) return false;
    if (f.iceOnly && isEV) return false;
    return true;
  });
}

export function isFieldEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '' || value === 0;
}
